// Hijri (Islamic civil/tabular) calendar conversion — ported from the
// previous Emrouz (Fourmilab algorithm) — corrected against moon sighting by
// the shifts in configs/hijri-adjustments.ts: a +1 shift means the month was
// observed to start one day later than the tabular date. A shifted start
// also stretches/shrinks the adjacent months, so both the conversion and
// month lengths apply the shifts of the two months around each boundary.
//
// The active shift table starts as the built-in list; services/hijri-adjustments
// swaps in the resolved (server/local/built-in) list once it has one.

import { BUILT_IN_ADJUSTMENTS, HijriAdjustment } from '../configs/hijri-adjustments'
import { IGregorianDate } from './jalaali'

export interface IHijriDate {
    hy: number
    hm: number
    hd: number
}

const GREGORIAN_EPOCH = 1721425.5
const ISLAMIC_EPOCH = 1948439.5

function isLeapGregorian(gy: number) {
    return gy % 4 === 0 && !(gy % 100 === 0 && gy % 400 !== 0)
}

function gregorianToJdt(gy: number, gm: number, gd: number) {
    return (GREGORIAN_EPOCH - 1) +
        (365 * (gy - 1)) +
        Math.floor((gy - 1) / 4) -
        Math.floor((gy - 1) / 100) +
        Math.floor((gy - 1) / 400) +
        Math.floor((((367 * gm) - 362) / 12) + (gm <= 2 ? 0 : isLeapGregorian(gy) ? -1 : -2) + gd)
}

function islamicToJdt(hy: number, hm: number, hd: number) {
    return (hd +
        Math.ceil(29.5 * (hm - 1)) +
        (hy - 1) * 354 +
        Math.floor((3 + (11 * hy)) / 30) +
        ISLAMIC_EPOCH) - 1
}

function jdtToIslamic(jdt: number): IHijriDate {
    jdt = Math.floor(jdt) + 0.5
    const hy = Math.floor(((30 * (jdt - ISLAMIC_EPOCH)) + 10646) / 10631)
    const hm = Math.min(12, Math.ceil((jdt - (29 + islamicToJdt(hy, 1, 1))) / 29.5) + 1)
    const hd = (jdt - islamicToJdt(hy, hm, 1)) + 1
    return { hy, hm, hd }
}

// Shifts indexed by month so a long downloaded list costs no more than the
// built-in one — every grid cell converts through here.
const indexed = (list: HijriAdjustment[]) =>
    new Map(list.map(([hy, hm, shift]) => [hy * 12 + hm, shift]))

let shifts = indexed(BUILT_IN_ADJUSTMENTS)

export function setHijriAdjustments(list: HijriAdjustment[]) {
    shifts = indexed(list)
}

function shiftOf(hy: number, hm: number) {
    return shifts.get(hy * 12 + hm) || 0
}

function nextMonth(hy: number, hm: number): [number, number] {
    return hm === 12 ? [hy + 1, 1] : [hy, hm + 1]
}

function prevMonth(hy: number, hm: number): [number, number] {
    return hm === 1 ? [hy - 1, 12] : [hy, hm - 1]
}

// Observed month length: the tabular length corrected by the shifts of this
// month's start and the next month's start.
export function hijriMonthLength(hy: number, hm: number): number {
    const [ny, nm] = nextMonth(hy, hm)
    return islamicToJdt(ny, nm, 1) - islamicToJdt(hy, hm, 1) - shiftOf(hy, hm) + shiftOf(ny, nm)
}

export function toHijri(gy: number, gm: number, gd: number): IHijriDate {
    const t = jdtToIslamic(gregorianToJdt(gy, gm, gd))
    const hd = t.hd - shiftOf(t.hy, t.hm)
    if (hd < 1) {
        // Before the observed start: still the previous month (hd is 0 for a
        // one-day shift, so this lands on its last day)
        const [py, pm] = prevMonth(t.hy, t.hm)
        return { hy: py, hm: pm, hd: hijriMonthLength(py, pm) + hd }
    }
    const days = hijriMonthLength(t.hy, t.hm)
    if (hd > days) {
        // Past the observed end: the next month already began
        const [ny, nm] = nextMonth(t.hy, t.hm)
        return { hy: ny, hm: nm, hd: hd - days }
    }
    return { hy: t.hy, hm: t.hm, hd }
}

// The inverse of toHijri, for callers that start from a hijri date (the
// converter). The month's observed start is its tabular start moved by the
// month's own shift; the day is counted from there. Walking a Date from a fixed
// anchor keeps the Gregorian arithmetic the platform's rather than a second
// hand-rolled algorithm.
const ANCHOR_JDT = gregorianToJdt(2000, 1, 1)

export function fromHijri(hy: number, hm: number, hd: number): IGregorianDate {
    const days = islamicToJdt(hy, hm, 1) + shiftOf(hy, hm) + hd - 1 - ANCHOR_JDT
    const date = new Date(2000, 0, 1 + days)
    return { gy: date.getFullYear(), gm: date.getMonth() + 1, gd: date.getDate() }
}
